/**
 * Typed client for `/api/v1/connectors/*` (CONNECTORS-SPEC §9).
 *
 * Five connectors land under one trait surface in the engine; this
 * file is the UI's binding. Today only `listConnectors` and
 * `createFileUploadProof` are wired server-side — the rest return
 * 501 Not Yet Implemented until the API server's token-store is
 * threaded through `AppState` (Phase 4.6).
 *
 * Auth: JSON-body endpoints go through `apiFetch` so they pick up
 * the standard X-AXIOM-* signing headers. The multipart upload
 * endpoint uses plain `fetch` with the bearer token only — request-
 * body signing for multipart payloads is its own design problem
 * (signing the boundary is brittle); the route is gated to
 * authenticated users via the bearer token, which is enough for the
 * Phase 4.5 UI flow.
 */

import { apiFetch } from '@/api/http';

// ─── Types — mirror server-side `ConnectorRow` / `SignedConnectorProof` ─

/** Closed enum of connector slugs. Server's
 *  `gi_connectors::ConnectorId::slug()` snake_case form. */
export type ConnectorSlug =
  | 'file_upload'
  | 'google_drive'
  | 'gmail'
  | 'google_calendar'
  | 'stripe';

/** `"oauth2"` | `"api_key"` | `"public"`. */
export type ConnectorAuthKind = 'oauth2' | 'api_key' | 'public';

/** One row from `GET /api/v1/connectors`. */
export interface ConnectorRow {
  /** Snake-case slug. */
  id: ConnectorSlug;
  /** Display name shown on the cards. */
  display_name: string;
  /** Drives the connect-button widget. */
  auth_kind: ConnectorAuthKind;
  /** Per-connector module SemVer. */
  version: string;
}

export interface ListConnectorsResponse {
  connectors: ConnectorRow[];
}

/** `ConnectionHandle` — the OAuth-redirect or already-connected variant. */
export type ConnectionHandle =
  | {
      kind: 'oauth_redirect';
      authorize_url: string;
      state: string;
    }
  | {
      kind: 'connected';
      user_handle: string;
      connector: ConnectorSlug;
    };

/** Server response from `connect`. */
export interface ConnectResponse {
  handle: ConnectionHandle;
}

/** Full proof JSON envelope. */
export interface SignedConnectorProof {
  proof_version: string;
  proof_id: string;
  connector: { id: string; version: string };
  captured_at: string;
  signed_by: { kind: 'engine' | 'operator'; pubkey: string };
  subject: { kind: string; [key: string]: unknown };
  subject_hash: string;
  signature: string;
}

/** Response from any `create_proof` endpoint. */
export interface CreateProofResponse {
  proof: SignedConnectorProof;
}

/** A single item returned by `list_items`. */
export interface ConnectorItem {
  id: string;
  display: string;
  created_at: string;
  kind: string;
}

// ─── HTTP wrappers ──────────────────────────────────────────────────

/** `GET /api/v1/connectors` — catalogue. */
export async function listConnectors(): Promise<ListConnectorsResponse> {
  return apiFetch<ListConnectorsResponse>('/connectors');
}

/** `POST /api/v1/connectors/{id}/connect` — initiate OAuth or validate API key. */
export async function connectConnector(
  id: ConnectorSlug,
  body: { api_key?: string; label?: string } = {},
): Promise<ConnectResponse> {
  return apiFetch<ConnectResponse>(`/connectors/${id}/connect`, {
    method: 'POST',
    body,
  });
}

/** `POST /api/v1/connectors/{id}/disconnect`. The server uses the
 *  handle to look up the user's stored tokens. */
export async function disconnectConnector(
  id: ConnectorSlug,
  handle: ConnectionHandle,
): Promise<void> {
  await apiFetch<void>(`/connectors/${id}/disconnect`, {
    method: 'POST',
    body: { handle },
  });
}

/** `GET /api/v1/connectors/{id}/items`. `userHandle` is the
 *  `Connected.user_handle` returned by `connectConnector` /
 *  `completeOAuth` and threaded through localStorage. */
export async function listConnectorItems(
  id: ConnectorSlug,
  userHandle: string,
  params: { limit?: number; query?: string; cursor?: string } = {},
): Promise<ConnectorItem[]> {
  const qs = new URLSearchParams();
  qs.set('user_handle', userHandle);
  if (params.limit !== undefined) qs.set('limit', String(params.limit));
  if (params.query !== undefined) qs.set('query', params.query);
  if (params.cursor !== undefined) qs.set('cursor', params.cursor);
  return apiFetch<ConnectorItem[]>(
    `/connectors/${id}/items?${qs.toString()}`,
  );
}

/** `POST /api/v1/connectors/{id}/proofs` — generic create_proof for
 *  pull-model connectors (Drive / Gmail / Calendar / Stripe). */
export async function createConnectorProof(
  id: ConnectorSlug,
  body: { handle: ConnectionHandle; item_id: string },
): Promise<CreateProofResponse> {
  return apiFetch<CreateProofResponse>(`/connectors/${id}/proofs`, {
    method: 'POST',
    body,
  });
}

/** `POST /api/v1/connectors/{id}/oauth/complete` — finishes the
 *  Google OAuth flow after the SPA's redirect handler picks up
 *  `?code=&state=`. Returns a `Connected` handle. */
export async function completeOAuth(
  id: ConnectorSlug,
  body: { code: string; state: string },
): Promise<ConnectResponse> {
  return apiFetch<ConnectResponse>(`/connectors/${id}/oauth/complete`, {
    method: 'POST',
    body,
  });
}

/** `POST /api/v1/connectors/file_upload/proofs` — multipart upload.
 *
 *  The browser caps the upload at 100 MiB to match the server's
 *  connector-side cap (CONNECTORS-SPEC §8.5.4 / Q-13). */
export async function createFileUploadProof(
  file: File,
): Promise<CreateProofResponse> {
  const MAX = 100 * 1024 * 1024;
  if (file.size > MAX) {
    throw new Error(
      `file too large (${file.size}B > ${MAX}B / 100 MiB)`,
    );
  }
  const form = new FormData();
  form.append('file', file, file.name);

  // Multipart bypasses `apiFetch` because the X-AXIOM-* signing layer
  // is JSON-body-shaped. The bearer token + auth middleware still
  // gate the route — see api/server `auth_middleware`.
  const baseUrl = readBaseUrl();
  const bearer = readBearerToken();
  const headers: Record<string, string> = {};
  if (bearer) headers['Authorization'] = `Bearer ${bearer}`;
  const resp = await fetch(`${baseUrl}/api/v1/connectors/file_upload/proofs`, {
    method: 'POST',
    headers,
    body: form,
  });
  if (!resp.ok) {
    let detail: string | undefined;
    try {
      const body = (await resp.json()) as { detail?: string };
      detail = body.detail;
    } catch {
      // RFC 7807 may be absent on edge errors.
    }
    throw new Error(
      `createFileUploadProof: HTTP ${resp.status}${detail ? ` — ${detail}` : ''}`,
    );
  }
  return (await resp.json()) as CreateProofResponse;
}

// ─── Internals ──────────────────────────────────────────────────────

function readBaseUrl(): string {
  // Mirror http.ts's resolution: VITE_API_BASE_URL → fallback to
  // current origin. Kept inline because http.ts doesn't export this
  // symbol publicly.
  const env = (import.meta.env.VITE_API_BASE_URL as string | undefined)?.trim();
  if (env) return env.replace(/\/+$/, '');
  return typeof window === 'undefined' ? '' : window.location.origin;
}

function readBearerToken(): string | null {
  // Mirror the auth flow: Supabase access JWT lives in localStorage
  // under the project-ref scoped key. http.ts has the canonical
  // resolver; here we just read the value http.ts already wrote.
  try {
    const k = 'gi-engine.access-token';
    return localStorage.getItem(k);
  } catch {
    return null;
  }
}
